import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";

// Enhanced animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(60px);
    filter: blur(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
    filter: blur(3px);
  }
  to {
    opacity: 1;
    transform: scale(1);
    filter: blur(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50px);
    filter: blur(3px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
    filter: blur(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(50px);
    filter: blur(3px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
    filter: blur(0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 191, 255, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(0, 191, 255, 0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const ProductsContainer = styled.div`
  padding: 6rem 0;
  background: linear-gradient(135deg, #f8fbff 0%, #f0f7ff 50%, #e8f3ff 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        circle at 20% 20%,
        rgba(0, 191, 255, 0.1) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 80% 80%,
        rgba(30, 144, 255, 0.08) 0%,
        transparent 40%
      );
    pointer-events: none;
  }

  @media (max-width: 1024px) {
    padding: 5rem 0;
  }

  @media (max-width: 768px) {
    padding: 4rem 0;
  }

  @media (max-width: 480px) {
    padding: 3rem 0;
  }
`;

const Section = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  margin-bottom: 6rem;
  position: relative;
  z-index: 2;

  @media (max-width: 1024px) {
    padding: 0 18px;
    margin-bottom: 5rem;
  }

  @media (max-width: 768px) {
    padding: 0 16px;
    margin-bottom: 4rem;
  }

  @media (max-width: 480px) {
    padding: 0 12px;
    margin-bottom: 3rem;
  }
`;

const QualityBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background: linear-gradient(
    135deg,
    rgba(0, 191, 255, 0.12),
    rgba(30, 144, 255, 0.08)
  );
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  color: var(--primary-blue);
  padding: 0.8rem 1.5rem;
  border-radius: 50px;
  font-size: 0.95rem;
  font-weight: 700;
  margin-bottom: 2.5rem;
  border: 2px solid rgba(0, 191, 255, 0.2);
  box-shadow: 0 8px 25px rgba(0, 191, 255, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${fadeInUp} 0.8s ease-out;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    display: inline-block;
    width: 20px;
    height: 20px;
    margin-right: 0.7rem;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23ffa726'%3E%3Cpath d='M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z'/%3E%3C/svg%3E") no-repeat center;
    background-size: contain;
    font-size: 1.2rem;
    animation: ${pulse} 2s ease infinite;
    text-shadow: 0 0 10px rgba(255, 167, 38, 0.5);
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    transition: left 0.6s ease;
  }

  &:hover {
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 15px 40px rgba(0, 191, 255, 0.25),
      inset 0 1px 0 rgba(255, 255, 255, 0.8);
    border-color: var(--primary-blue);

    &::after {
      left: 100%;
    }
  }
`;

const SectionTitle = styled.h2`
  font-size: 3rem;
  font-weight: 800;
  color: var(--text-primary);
  text-align: center;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #1a1a1a, #333333, #1a1a1a);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${fadeInUp} 1s ease-out 0.2s both;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, #00bfff, #1e90ff);
    border-radius: 2px;
    animation: ${fadeInScale} 0.8s ease-out 0.8s both;
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: var(--text-secondary);
  text-align: center;
  max-width: 700px;
  margin: 0 auto 4rem;
  line-height: 1.8;
  animation: ${fadeInUp} 1s ease-out 0.4s both;
  font-weight: 400;

  .highlight {
    color: var(--primary-blue);
    font-weight: 700;
    background: linear-gradient(135deg, transparent, rgba(0, 191, 255, 0.1));
    padding: 2px 8px;
    border-radius: 6px;
    transition: all 0.3s ease;

    &:hover {
      background: linear-gradient(
        135deg,
        rgba(0, 191, 255, 0.15),
        rgba(30, 144, 255, 0.1)
      );
      transform: scale(1.05);
    }
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin: 0 auto 3rem;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-bottom: 4rem;
  }

  @media (max-width: 480px) {
    gap: 1.2rem;
  }
`;

const ProductCard = styled.div`
  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.95),
    rgba(255, 255, 255, 0.9)
  );
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 25px;
  padding: 2.5rem;
  box-shadow: 0 15px 50px rgba(0, 191, 255, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  text-align: center;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid rgba(0, 191, 255, 0.1);
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.8s ease-out both;

  &:nth-child(1) {
    animation-delay: 0.1s;
  }
  &:nth-child(2) {
    animation-delay: 0.2s;
  }
  &:nth-child(3) {
    animation-delay: 0.3s;
  }
  &:nth-child(4) {
    animation-delay: 0.4s;
  }
  &:nth-child(5) {
    animation-delay: 0.5s;
  }
  &:nth-child(6) {
    animation-delay: 0.6s;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      transparent,
      var(--primary-blue),
      transparent
    );
    transform: scaleX(0);
    transition: transform 0.5s ease;
    border-radius: 25px 25px 0 0;
  }

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    background: radial-gradient(
      circle,
      rgba(0, 191, 255, 0.05) 0%,
      transparent 70%
    );
    border-radius: 50%;
    transform: translate(-50%, -50%);
    transition: width 0.6s ease, height 0.6s ease;
    z-index: -1;
  }

  &:hover {
    transform: translateY(-10px) scale(1.03);
    box-shadow: 0 25px 80px rgba(0, 191, 255, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 1);
    border-color: var(--primary-blue);
    background: linear-gradient(
      145deg,
      rgba(255, 255, 255, 1),
      rgba(255, 255, 255, 0.98)
    );

    &::before {
      transform: scaleX(1);
    }

    &::after {
      width: 400px;
      height: 400px;
    }
  }

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const ProductIcon = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  color: white;
  font-size: 2.5rem;
  position: relative;
  transition: all 0.4s ease;
  z-index: 2;

  &::before {
    content: "";
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border-radius: 50%;
    opacity: 0;
    transition: opacity 0.4s ease;
  }

  &.superior {
    background: linear-gradient(135deg, #00bfff, #1e90ff, #0066cc);
    &::before {
      background: linear-gradient(135deg, #00bfff, #1e90ff);
    }
  }
  &.dry {
    background: linear-gradient(135deg, #ff6b35, #ff8c42, #ffa726);
    &::before {
      background: linear-gradient(135deg, #ff6b35, #ff8c42);
    }
  }
  &.natural {
    background: linear-gradient(135deg, #4caf50, #66bb6a, #81c784);
    &::before {
      background: linear-gradient(135deg, #4caf50, #66bb6a);
    }
  }
  &.comfort {
    background: linear-gradient(135deg, #9c27b0, #ba68c8, #ce93d8);
    &::before {
      background: linear-gradient(135deg, #9c27b0, #ba68c8);
    }
  }
  &.ergonomic {
    background: linear-gradient(135deg, #ff5722, #ff7043, #ff8a65);
    &::before {
      background: linear-gradient(135deg, #ff5722, #ff7043);
    }
  }
  &.slim {
    background: linear-gradient(135deg, #2196f3, #42a5f5, #64b5f6);
    &::before {
      background: linear-gradient(135deg, #2196f3, #42a5f5);
    }
  }

  ${ProductCard}:hover & {
    transform: scale(1.1) rotateY(10deg);
    animation: ${float} 2s ease infinite;

    &::before {
      opacity: 0.3;
      animation: ${pulse} 2s ease infinite;
    }
  }

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
    font-size: 2rem;
  }
`;

const ProductTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 1.2rem;
  line-height: 1.3;
  z-index: 2;
  position: relative;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ProductDescription = styled.p`
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: 1.8rem;
  font-size: 1rem;
  z-index: 2;
  position: relative;
`;

const ProductFeature = styled.div`
  color: var(--primary-blue);
  font-weight: 700;
  font-size: 1rem;
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  background: linear-gradient(135deg, transparent, rgba(0, 191, 255, 0.1));
  display: inline-block;

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(0, 191, 255, 0.15),
      rgba(30, 144, 255, 0.1)
    );
    transform: translateX(5px);
  }
`;

const AdvantagesSection = styled.div`
  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.9),
    rgba(255, 255, 255, 0.8)
  );
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 30px;
  padding: 4rem;
  border: 2px solid rgba(0, 191, 255, 0.15);
  box-shadow: 0 20px 60px rgba(0, 191, 255, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 1s ease-out 0.6s both;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      #00bfff,
      #1e90ff,
      #0066cc,
      #1e90ff,
      #00bfff
    );
    background-size: 200% 100%;
    animation: ${shimmer} 3s ease infinite;
    border-radius: 30px 30px 0 0;
  }

  @media (max-width: 768px) {
    padding: 3rem 2rem;
  }

  @media (max-width: 480px) {
    padding: 2.5rem 1.5rem;
  }
`;

const AdvantagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;

  @media (max-width: 1024px) {
    gap: 1.5rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.2rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const AdvantageCard = styled.div`
  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 1),
    rgba(255, 255, 255, 0.95)
  );
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 2.5rem 1.5rem;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 191, 255, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  border: 2px solid rgba(0, 191, 255, 0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  animation: ${slideInLeft} 0.8s ease-out both;

  &:nth-child(1) {
    animation-delay: 0.8s;
  }
  &:nth-child(2) {
    animation-delay: 0.9s;
    animation-name: ${slideInRight};
  }
  &:nth-child(3) {
    animation-delay: 1s;
  }
  &:nth-child(4) {
    animation-delay: 1.1s;
    animation-name: ${slideInRight};
  }

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    background: radial-gradient(
      circle,
      rgba(0, 191, 255, 0.05) 0%,
      transparent 70%
    );
    border-radius: 50%;
    transform: translate(-50%, -50%);
    transition: width 0.5s ease, height 0.5s ease;
    z-index: 0;
  }

  &:hover {
    transform: translateY(-8px) scale(1.05);
    box-shadow: 0 20px 50px rgba(0, 191, 255, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 1);
    border-color: var(--primary-blue);

    &::after {
      width: 200px;
      height: 200px;
    }
  }

  @media (max-width: 768px) {
    padding: 2rem 1.2rem;
  }
`;

const AdvantageIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  color: white;
  font-size: 2rem;
  font-weight: bold;
  position: relative;
  z-index: 2;
  transition: all 0.4s ease;

  &.layers {
    background: linear-gradient(135deg, #4caf50, #66bb6a, #81c784);
  }
  &.anion {
    background: linear-gradient(135deg, #00bfff, #1e90ff, #0066cc);
  }
  &.gelly {
    background: linear-gradient(135deg, #4caf50, #66bb6a, #81c784);
  }
  &.satisfaction {
    background: linear-gradient(135deg, #ff6b35, #ff8c42, #ffa726);
  }

  ${AdvantageCard}:hover & {
    transform: scale(1.1) rotate(360deg);
    box-shadow: 0 0 30px rgba(0, 191, 255, 0.3);
  }

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    font-size: 1.5rem;
  }
`;

const AdvantageNumber = styled.div`
  font-size: 2rem;
  font-weight: 900;
  color: var(--text-primary);
  margin-bottom: 0.8rem;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const AdvantageLabel = styled.div`
  color: var(--text-primary);
  font-weight: 700;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const AdvantageSubtext = styled.div`
  color: var(--text-secondary);
  font-size: 0.9rem;
  font-weight: 500;
  position: relative;
  z-index: 2;
`;

const ProductGalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1.2rem;
  }
`;

const AchievementsGrid = styled.div`
  background: #f8f9fa;
  border-radius: 20px;
  padding: 3rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 2rem;
  margin-top: 4rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    padding: 2.5rem;
  }

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
    padding: 2.5rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    padding: 2rem;
    gap: 1.5rem;
    border-radius: 15px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const AchievementCard = styled.div`
  text-align: center;
  background: white;
  padding: 2rem 1rem;
  border-radius: 15px;
  box-shadow: 0 2px 10px rgba(0, 191, 255, 0.1);
  border: 1px solid #f8f9fa;

  @media (max-width: 1024px) {
    padding: 1.8rem 0.8rem;
  }

  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
    border-radius: 12px;
  }

  @media (max-width: 480px) {
    padding: 1.3rem 0.8rem;
    border-radius: 10px;
  }
`;

const AchievementNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #00bfff;
  margin-bottom: 0.5rem;

  @media (max-width: 1024px) {
    font-size: 2.2rem;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 0.4rem;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin-bottom: 0.3rem;
  }
`;

const AchievementLabel = styled.div`
  color: #2c3e50;
  font-weight: 600;
  margin-bottom: 0.25rem;
  font-size: 0.95rem;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
    margin-bottom: 0.2rem;
  }
`;

const AchievementSubtext = styled.div`
  color: #5a6c7d;
  font-size: 0.85rem;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const ProductImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  background: white;
  box-shadow: 0 10px 30px rgba(0, 191, 255, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  border: 2px solid rgba(0, 191, 255, 0.1);
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${fadeInScale} 0.8s ease-out both;
  cursor: pointer;

  &:nth-child(1) {
    animation-delay: 0.1s;
  }
  &:nth-child(2) {
    animation-delay: 0.2s;
  }
  &:nth-child(3) {
    animation-delay: 0.3s;
  }
  &:nth-child(4) {
    animation-delay: 0.4s;
  }
  &:nth-child(5) {
    animation-delay: 0.5s;
  }
  &:nth-child(6) {
    animation-delay: 0.6s;
  }
  &:nth-child(7) {
    animation-delay: 0.7s;
  }
  &:nth-child(8) {
    animation-delay: 0.8s;
  }
  &:nth-child(9) {
    animation-delay: 0.9s;
  }
  &:nth-child(10) {
    animation-delay: 1s;
  }
  &:nth-child(11) {
    animation-delay: 1.1s;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.6),
      transparent
    );
    transition: left 0.6s ease;
    z-index: 2;
  }

  &:hover {
    transform: translateY(-10px) scale(1.05);
    box-shadow: 0 20px 60px rgba(0, 191, 255, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 1);
    border-color: var(--primary-blue);

    &::before {
      left: 100%;
    }
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 350px;
  object-fit: contain;
  padding: 2rem;
  transition: all 0.5s ease;
  position: relative;
  z-index: 1;

  @media (max-width: 1024px) {
    height: 300px;
    padding: 1.5rem;
  }

  @media (max-width: 768px) {
    height: 280px;
    padding: 1.2rem;
  }

  @media (max-width: 480px) {
    height: 250px;
    padding: 1rem;
  }
`;

// Intersection Observer Hook
const useInView = (options) => {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, options);

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [options]);

  return [ref, isInView];
};

const AnimatedSection = ({ children, ...props }) => {
  const [ref, isInView] = useInView({ threshold: 0.1 });

  return (
    <div
      ref={ref}
      style={{ opacity: isInView ? 1 : 0.3, transition: "opacity 0.6s ease" }}
      {...props}
    >
      {children}
    </div>
  );
};

const Products = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const productImages = [
    {
      src: require("../assets/products/IMG-20250910-WA0079.jpg"),
      alt: "ASKK Premium Sanitary Pads - Pack View",
    },
    {
      src: require("../assets/products/IMG-20250910-WA0080.jpg"),
      alt: "ASKK 7-Layer Cotton Technology",
    },
    {
      src: require("../assets/products/IMG-20250910-WA0081.jpg"),
      alt: "ASKK Anion Chip Technology",
    },
    {
      src: require("../assets/products/IMG-20250910-WA0082.jpg"),
      alt: "ASKK Natural Aloe Vera Formula",
    },
    {
      src: require("../assets/products/IMG-20250910-WA0083.jpg"),
      alt: "ASKK Ultra-Thin Design",
    },
    {
      src: require("../assets/products/IMG-20250910-WA0084.jpg"),
      alt: "ASKK Superior Absorption",
    },
    {
      src: require("../assets/products/IMG-20250910-WA0085.jpg"),
      alt: "ASKK Comfort Technology",
    },
    {
      src: require("../assets/products/IMG-20250910-WA0086.jpg"),
      alt: "ASKK Premium Quality",
    },
    {
      src: require("../assets/products/IMG-20250910-WA0087.jpg"),
      alt: "ASKK Women's Health Solution",
    },
    {
      src: require("../assets/products/IMG-20250910-WA0088.jpg"),
      alt: "ASKK Complete Protection",
    },
    {
      src: require("../assets/products/IMG-20250910-WA0089.jpg"),
      alt: "ASKK Trusted by Women",
    },
  ];

  return (
    <ProductsContainer>
      <AnimatedSection>
        <Section>
          <SectionTitle>Product Gallery</SectionTitle>
          <SectionSubtitle>
            Explore our premium ASKK sanitary pads collection - designed with
            women's comfort in mind
          </SectionSubtitle>

          <ProductGalleryGrid>
            {productImages.map((image, index) => (
              <ProductImageContainer
                key={index}
                onClick={() => setSelectedImage(image)}
              >
                <ProductImage src={image.src} alt={image.alt} loading="lazy" />
              </ProductImageContainer>
            ))}
          </ProductGalleryGrid>

          <AchievementsGrid>
            <AchievementCard>
              <AchievementNumber className="customers">10L+</AchievementNumber>
              <AchievementLabel>Satisfied Customers</AchievementLabel>
              <AchievementSubtext>Across 5 States</AchievementSubtext>
            </AchievementCard>

            <AchievementCard>
              <AchievementNumber className="states">5</AchievementNumber>
              <AchievementLabel>States Coverage</AchievementLabel>
              <AchievementSubtext>Central & Western India</AchievementSubtext>
            </AchievementCard>

            <AchievementCard>
              <AchievementNumber className="experience">4+</AchievementNumber>
              <AchievementLabel>Years Experience</AchievementLabel>
              <AchievementSubtext>Since April 2021</AchievementSubtext>
            </AchievementCard>

            <AchievementCard>
              <AchievementNumber className="satisfaction">
                100%
              </AchievementNumber>
              <AchievementLabel>Satisfaction</AchievementLabel>
              <AchievementSubtext>Guaranteed Quality</AchievementSubtext>
            </AchievementCard>
          </AchievementsGrid>
        </Section>
      </AnimatedSection>

      <AnimatedSection>
        <Section>
          <div className="text-center">
            <QualityBadge>Premium Quality Products</QualityBadge>
            <SectionTitle>ASKK Sanitary Pads</SectionTitle>
            <SectionSubtitle>
              <span className="highlight">Real Advantages for Real Women</span>{" "}
              — Experience the revolutionary difference with our advanced
              technology and natural ingredients
            </SectionSubtitle>
          </div>

          <ProductGrid>
            <ProductCard>
              <ProductIcon className="superior">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7C13.4,7 14.8,8.6 14.8,10V11.5C15.4,11.9 16,12.4 16,13V16C16,17.1 15.1,18 14,18H10C8.9,18 8,17.1 8,16V13C8,12.4 8.4,11.9 9,11.5V10C9,8.6 10.6,7 12,7M12,8.2C11.2,8.2 10.2,8.7 10.2,10V11.5H13.8V10C13.8,8.7 12.8,8.2 12,8.2Z" fill="white" />
                </svg>
              </ProductIcon>
              <ProductTitle>Freedom from Frequent Changing</ProductTitle>
              <ProductDescription>
                Experience unparalleled comfort with our superior absorption
                technology that keeps you protected longer, giving you the
                confidence to focus on what matters most.
              </ProductDescription>
              <ProductFeature>Superior Absorption →</ProductFeature>
            </ProductCard>

            <ProductCard>
              <ProductIcon className="dry">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="white" />
                </svg>
              </ProductIcon>
              <ProductTitle>Relief from Stickiness & Wetness</ProductTitle>
              <ProductDescription>
                Say goodbye to uncomfortable wetness with our advanced dry
                surface technology that keeps you feeling fresh and confident
                throughout your day.
              </ProductDescription>
              <ProductFeature>Dry Technology →</ProductFeature>
            </ProductCard>

            <ProductCard>
              <ProductIcon className="natural">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22L6.66 19.7C7.14 19.87 7.64 20 8 20C19 20 22 3 22 3C21 5 14 5.25 9 6.25C4 7.25 2 11.5 2 13.5C2 15.5 3.75 17.25 3.75 17.25C7.5 15 8.5 14.5 8.5 14.5L17 8Z" fill="white" />
                </svg>
              </ProductIcon>
              <ProductTitle>Relief from Itching & Burning</ProductTitle>
              <ProductDescription>
                Naturally soothing relief with our premium Aloe Vera and Papaya
                Gelly formula that prevents irritation and promotes healthy skin
                comfort.
              </ProductDescription>
              <ProductFeature>Natural Care →</ProductFeature>
            </ProductCard>

            <ProductCard>
              <ProductIcon className="comfort">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6,2L2,8L12,22L22,8L18,2H6M6.5,4H17.5L20.5,8.5L12,19L3.5,8.5L6.5,4M8.5,6L6,10H10L12.5,6H8.5M15.5,6H11.5L9,10H15L17.5,6H15.5Z" fill="white" />
                </svg>
              </ProductIcon>
              <ProductTitle>Relief from Rashes</ProductTitle>
              <ProductDescription>
                Advanced Anion technology combined with ergonomic comfort design
                provides gentle protection that prevents rashes and promotes
                wellness.
              </ProductDescription>
              <ProductFeature>Comfort Design →</ProductFeature>
            </ProductCard>

            <ProductCard>
              <ProductIcon className="ergonomic">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor" />
                </svg>
              </ProductIcon>
              <ProductTitle>Relief from Back Pain</ProductTitle>
              <ProductDescription>
                Ergonomically designed for optimal support and improved blood
                circulation, helping to reduce discomfort and back pain during
                your cycle.
              </ProductDescription>
              <ProductFeature>Ergonomic Support →</ProductFeature>
            </ProductCard>

            <ProductCard>
              <ProductIcon className="slim">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M12,6A6,6 0 0,0 6,12A6,6 0 0,0 12,18A6,6 0 0,0 18,12A6,6 0 0,0 12,6M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8M12,10A2,2 0 0,0 10,12A2,2 0 0,0 12,14A2,2 0 0,0 14,12A2,2 0 0,0 12,10Z" fill="white" />
                </svg>
              </ProductIcon>
              <ProductTitle>Slim & Lightweight Design</ProductTitle>
              <ProductDescription>
                7-layer pure cotton technology in an ultra-thin design that
                moves with you, providing maximum protection without
                compromising on comfort or mobility.
              </ProductDescription>
              <ProductFeature>Ultra-Thin Design →</ProductFeature>
            </ProductCard>
          </ProductGrid>
        </Section>
      </AnimatedSection>

      <AnimatedSection>
        <Section>
          <SectionTitle>Why Choose ASKK Sanitary Pads?</SectionTitle>
          <SectionSubtitle>
            Revolutionary features that transform your menstrual experience with
            science-backed comfort
          </SectionSubtitle>

          <AdvantagesSection>
            <AdvantagesGrid>
              <AdvantageCard>
                <AdvantageIcon className="layers">7</AdvantageIcon>
                <AdvantageNumber>7</AdvantageNumber>
                <AdvantageLabel>Layer Cotton</AdvantageLabel>
                <AdvantageSubtext>Pure & Ultra-Soft</AdvantageSubtext>
              </AdvantageCard>

              <AdvantageCard>
                <AdvantageIcon className="anion">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </AdvantageIcon>
                <AdvantageNumber>J+</AdvantageNumber>
                <AdvantageLabel>Anion Chip</AdvantageLabel>
                <AdvantageSubtext>Advanced Technology</AdvantageSubtext>
              </AdvantageCard>

              <AdvantageCard>
                <AdvantageIcon className="gelly">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22L6.66 19.7C7.14 19.87 7.64 20 8 20C19 20 22 3 22 3C21 5 14 5.25 9 6.25C4 7.25 2 11.5 2 13.5C2 15.5 3.75 17.25 3.75 17.25C7.5 15 8.5 14.5 8.5 14.5L17 8Z" fill="white" />
                  </svg>
                </AdvantageIcon>
                <AdvantageNumber>✓</AdvantageNumber>
                <AdvantageLabel>Natural Gelly</AdvantageLabel>
                <AdvantageSubtext>Aloe Vera & Papaya</AdvantageSubtext>
              </AdvantageCard>

              <AdvantageCard>
                <AdvantageIcon className="satisfaction">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M15.59,7L12,10.59L8.41,7L7,8.41L10.59,12L7,15.59L8.41,17L12,13.41L15.59,17L17,15.59L13.41,12L17,8.41L15.59,7Z" fill="white" />
                  </svg>
                </AdvantageIcon>
                <AdvantageNumber>100%</AdvantageNumber>
                <AdvantageLabel>Satisfaction</AdvantageLabel>
                <AdvantageSubtext>Guaranteed Quality</AdvantageSubtext>
              </AdvantageCard>
            </AdvantagesGrid>
          </AdvantagesSection>
        </Section>
      </AnimatedSection>

      {/* Enhanced Modal for Image Preview */}
      {selectedImage && (
        <ImageModal onClick={() => setSelectedImage(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setSelectedImage(null)}>×</CloseButton>
            <ModalImage src={selectedImage.src} alt={selectedImage.alt} />
            <ModalCaption>{selectedImage.alt}</ModalCaption>
          </ModalContent>
        </ImageModal>
      )}
    </ProductsContainer>
  );
};

// Modal Components
const ImageModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: ${fadeInScale} 0.3s ease-out;
  cursor: pointer;
`;

const ModalContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background: white;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  cursor: default;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 10000;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }
`;

const ModalImage = styled.img`
  width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 10px;
`;

const ModalCaption = styled.p`
  text-align: center;
  margin-top: 1rem;
  color: var(--text-secondary);
  font-weight: 600;
`;

export default Products;
